import { useCallback, useMemo, useRef, useState } from "react";
import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useAppPermissionQuery } from "../../../permissions";
import {
  Button,
  cn,
  useContainerWidth,
  useStateWithSetAction,
  type ActionButtonConfig,
  type VirtualizedColumnDef,
  type VirtualizedDataTableProps,
} from "@tellescope/ui";
import { useCallingShallowStore, useCallQueueShallowStore } from "../../../store";
import { useTranslation } from "react-i18next";
import { createKeyMap } from "@tellescope/utils";
import { Pencil, PhoneCall, RefreshCw, Volume2, VolumeX } from "lucide-react";

type CallHoldQueue = TellescopeModelFromName<"call_hold_queues">;
type CallQueueRow = CallHoldQueue & {
  currentSize: number;
  averageWaitTime: number;
};

interface UseCallQueuesPanelOptions {
  playNewCallSound?: () => void;
}

export const useCallQueuesPanel = ({ playNewCallSound }: UseCallQueuesPanelOptions = {}) => {
  const { t } = useTranslation("call-queues");
  const { ref: containerRef, width: containerWidth } = useContainerWidth<HTMLDivElement>();
  const isCompact = containerWidth > 0 && containerWidth < 650;
  const { currentUser, selectedOrganization } = useAuth();
  const { queueIndexes, setQueueIndexes, playSoundOnNewCall, setPlaySoundOnNewCall } = useCallQueueShallowStore(
    "queueIndexes",
    "setQueueIndexes",
    "setPlaySoundOnNewCall",
    "playSoundOnNewCall"
  );
  const { activeCall, incomingCalls } = useCallingShallowStore("activeCall", "incomingCalls");
  const [showFullList, setShowFullList] = useState(false);

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
  } = useAppPermissionQuery({ model: "call_hold_queues", actions: ["create", "update"] });
  const [editingQueueId, setEditingQueueId] = useState<string>();
  const [isCreateEditSheetOpen, setIsCreateEditSheetOpen] = useStateWithSetAction(false, (newVal) => {
    if (!newVal) {
      setEditingQueueId(undefined);
    }
  });

  const answerCallMutation = useTellescopeMutation({
    model: "call_hold_queues",
    method: "answer_call",
  });

  const loopQueueCallSound = selectedOrganization?.settings?.endusers?.loopQueueCallSound === true;

  // Track previous queue sizes to detect new calls in queues assigned to the current user
  const previousQueueSizesRef = useRef<Record<string, number>>({});
  const playNewCallSoundRef = useRef(playNewCallSound);
  playNewCallSoundRef.current = playNewCallSound;

  // Keep refs for values needed inside the loop tick so we always read the latest
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;
  const incomingCallsRef = useRef(incomingCalls);
  incomingCallsRef.current = incomingCalls;
  const playSoundOnNewCallRef = useRef(playSoundOnNewCall);
  playSoundOnNewCallRef.current = playSoundOnNewCall;
  const loopQueueCallSoundRef = useRef(loopQueueCallSound);
  loopQueueCallSoundRef.current = loopQueueCallSound;

  // Ref to track the loop interval so we don't start multiple loops
  const loopIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopLoop = useCallback(() => {
    if (loopIntervalRef.current !== null) {
      clearInterval(loopIntervalRef.current);
      loopIntervalRef.current = null;
    }
  }, []);

  const startLoop = useCallback(() => {
    if (loopIntervalRef.current !== null) return; // already looping
    loopIntervalRef.current = setInterval(() => {
      // Self-terminate if any stop condition is met (including when an incoming call window is displayed)
      if (
        activeCallRef.current ||
        incomingCallsRef.current.length > 0 ||
        !playSoundOnNewCallRef.current ||
        !loopQueueCallSoundRef.current
      ) {
        stopLoop();
        return;
      }
      playNewCallSoundRef.current?.();
    }, 2500);
  }, [stopLoop]);

  const callHoldQueuesQuery = useTellescopeInfiniteQuery({
    model: "call_hold_queues",
    method: "getSome",
    request: {
      limit: 500,
    },
    select: (data) =>
      data.pages.flat().sort((a, b) => {
        const aIndex = queueIndexes[a.id];
        const bIndex = queueIndexes[b.id];

        if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
        if (aIndex !== undefined) return -1;
        if (bIndex !== undefined) return 1;
        return 0;
      }),
  });

  // Build a set of queue IDs that the current user is assigned to
  const userQueueIds = useMemo(() => {
    const queues = callHoldQueuesQuery.data ?? [];
    const userId = currentUser?.id;
    if (!userId) return new Set<string>();
    return new Set(
      queues.reduce<string[]>((acc, curr) => {
        if (curr.userIds?.includes(userId)) {
          acc.push(curr.id);
        }
        return acc;
      }, [])
    );
  }, [callHoldQueuesQuery.data, currentUser?.id]);

  const userQueueIdsRef = useRef(userQueueIds);
  userQueueIdsRef.current = userQueueIds;

  const queueDetailsQuery = useTellescopeQuery({
    model: "call_hold_queues",
    method: "get_details",
    request: {},
    refetchInterval: 5000,
    responseSideEffect: (_, response) => {
      const assignedQueueIds = userQueueIdsRef.current;
      const prev = previousQueueSizesRef.current;
      const hasExistingData = Object.keys(prev).length > 0;

      const current: Record<string, number> = {};
      let hasNewCall = false;
      let callsInUserQueues = false;

      for (const queue of response.queues) {
        const isAssignedToUser = assignedQueueIds.has(queue.friendlyName);

        current[queue.friendlyName] = queue.currentSize;

        if (isAssignedToUser && queue.currentSize > 0) {
          callsInUserQueues = true;
        }

        if (isAssignedToUser && hasExistingData) {
          const previousSize = prev[queue.friendlyName] ?? 0;
          if (queue.currentSize > previousSize) {
            hasNewCall = true;
          }
        }
      }

      previousQueueSizesRef.current = current;

      if (!callsInUserQueues) {
        stopLoop();
        return;
      }

      if (hasNewCall && playSoundOnNewCallRef.current) {
        playNewCallSoundRef.current?.();

        // Start looping if the org setting is enabled and we're not already on a call or answering one
        if (loopQueueCallSoundRef.current && !activeCallRef.current && incomingCallsRef.current.length === 0) {
          startLoop();
        }
      }
    },
  });

  const { data, hiddenRowCount, hiddenRowsWithActiveCallsCount } = useMemo<{
    data: CallQueueRow[];
    hiddenRowCount: number;
    hiddenRowsWithActiveCallsCount: number;
  }>(() => {
    const queues = callHoldQueuesQuery.data ?? [];
    const detailsMap = createKeyMap(queueDetailsQuery.data?.queues ?? [], "friendlyName");

    const data = queues.map((queue) => {
      const details = detailsMap[queue.id ?? ""];
      return {
        ...queue,
        currentSize: details?.currentSize ?? 0,
        averageWaitTime: details?.averageWaitTime ?? 0,
      };
    });

    const hiddenRows = showFullList ? [] : data.slice(3);
    const hiddenRowsWithActiveCalls = hiddenRows.filter(({ currentSize }) => !!currentSize);

    return {
      data,
      hiddenRowCount: hiddenRows.length,
      hiddenRowsWithActiveCallsCount: hiddenRowsWithActiveCalls.length,
    };
  }, [callHoldQueuesQuery.data, queueDetailsQuery.data?.queues, showFullList]);

  const handleAnswerCall = useCallback(
    (queueId: string) => {
      answerCallMutation.mutate({ queueId });
    },
    [answerCallMutation]
  );

  const columns = useMemo<VirtualizedColumnDef<CallQueueRow>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        label: t("Title"),
        width: "2fr",
        minWidth: 150,
      },
      {
        id: "currentSize",
        accessorKey: "currentSize",
        label: t("Calls"),
        width: "1fr",
        minWidth: 100,
        cell: ({ getValue }) => {
          let value = getValue();
          if (typeof value !== "number") value = 0;
          return <>{value}</>;
        },
      },
      {
        id: "averageWaitTime",
        accessorKey: "averageWaitTime",
        label: t("Avg Wait (secs)"),
        width: "1fr",
        minWidth: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          if (typeof value !== "number") {
            return <>0</>;
          }
          return <>{value}</>;
        },
      },
      {
        id: "actions",
        label: "",
        width: isCompact ? 70 : 100,
        sortable: false,
        cell: ({ row }) => {
          const queue = row.original;
          return (
            <div
              className={cn("flex items-center gap-1 justify-end w-full", isCompact && "gap-0")}
              onClick={(e) => e.stopPropagation()}
            >
              {isCompact ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  hoverLabel={t("Answer Call")}
                  aria-label={t("Answer Call")}
                  disabled={queue.currentSize === 0 || answerCallMutation.isPending}
                  loading={answerCallMutation.isPending}
                  onClick={() => handleAnswerCall(queue.id)}
                  data-trackingid="call-queues-answer-call"
                >
                  <PhoneCall className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={queue.currentSize === 0 || answerCallMutation.isPending}
                  loading={answerCallMutation.isPending}
                  onClick={() => handleAnswerCall(queue.id)}
                  data-trackingid="call-queues-answer-call"
                >
                  {t("Answer")}
                </Button>
              )}
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  hoverLabel={t("Edit Queue")}
                  aria-label={t("Edit Queue")}
                  onClick={() => {
                    setEditingQueueId(queue.id);
                    setIsCreateEditSheetOpen(true);
                  }}
                  data-trackingid="call-queues-edit-queue"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [answerCallMutation.isPending, canUpdate, handleAnswerCall, isCompact, setIsCreateEditSheetOpen, t]
  );

  const handleReorder = useCallback(
    (reorderedItems: Array<{ id: string; index: number }>) => {
      const updated: Record<string, number> = {};
      reorderedItems.forEach((item) => {
        updated[item.id] = item.index;
      });
      setQueueIndexes(updated);
    },
    [setQueueIndexes]
  );

  const actionButtons = useMemo<ActionButtonConfig[]>(() => {
    const result: ActionButtonConfig[] = [
      {
        icon: RefreshCw,
        variant: "ghost",
        size: "icon",
        label: "",
        hoverLabel: t("Refresh"),
        onClick: () => {
          callHoldQueuesQuery.refetch();
          queueDetailsQuery.refetch();
        },
      },
      playSoundOnNewCall
        ? {
            icon: Volume2,
            variant: "ghost",
            size: "icon",
            label: "",
            hoverLabel: t("Mute sound when a new call is added to a queue"),
            onClick: () => setPlaySoundOnNewCall(false),
          }
        : {
            icon: VolumeX,
            variant: "ghost",
            size: "icon",
            label: "",
            hoverLabel: t("Play sound when a new call is added to a queue"),
            onClick: () => setPlaySoundOnNewCall(true),
          },
    ];
    if (canCreate) {
      result.push({
        label: t("Create Queue"),
        onClick: () => setIsCreateEditSheetOpen(true),
        trackingId: "call-queues-create-queue",
      });
    }
    return result;
  }, [
    callHoldQueuesQuery,
    canCreate,
    playSoundOnNewCall,
    queueDetailsQuery,
    setIsCreateEditSheetOpen,
    setPlaySoundOnNewCall,
    t,
  ]);

  const virtualizedTableProps = useMemo(
    () =>
      ({
        data,
        columns,
        getRowId: (row: CallQueueRow) => row.id,
        enableDragDrop: true,
        onReorder: handleReorder,
        actionButtons,
        maxDisplayRows: showFullList ? undefined : 3,
        hideFooter: true,
      }) satisfies Partial<VirtualizedDataTableProps<CallQueueRow>>,
    [actionButtons, columns, data, handleReorder, showFullList]
  );

  return {
    containerRef,
    virtualizedTableProps,
    isLoading: callHoldQueuesQuery.isLoading,
    isCreateEditSheetOpen,
    setIsCreateEditSheetOpen,
    editingQueueId,
    setEditingQueueId,
    showFullList,
    setShowFullList,
    hiddenRowCount,
    hiddenRowsWithActiveCallsCount,
  };
};
